import { guessCurrencyFromLocale } from '../../../utils/currencies';
import { summaryName } from '../../../utils/tripLegs';
import { EMPTY_TICKETS, type TicketFiles } from '../../../api/ticketImport';

export type Step = 'destination' | 'dates' | 'legs' | 'travellers' | 'bookings' | 'start' | 'documents' | 'style' | 'summary';
export type StartMode = 'ai' | 'documents' | 'empty';
export type Pace = 'relaxed' | 'balanced' | 'packed';
export type Budget = 'budget' | 'mid' | 'splurge';

export const PACES: Pace[] = ['relaxed', 'balanced', 'packed'];
export const BUDGETS: Budget[] = ['budget', 'mid', 'splurge'];
export const MAX_TRAVELLERS = 20;
/** A school trip (ADR-0033); the server allows the same. */
export const MAX_AGENCY_TRAVELLERS = 60;

/**
 * Who the trip is for. ADR-0033: staff create an agency's trip for its
 * clients — the first traveller is the lead, who owns it, and the employee is
 * not in it — or a circuit template, which has no travellers at all.
 */
export type WizardMode =
  | { kind: 'personal' }
  | { kind: 'agency'; agencyId: string; isTemplate: boolean };

export function maxTravellers(mode: WizardMode): number {
  return mode.kind === 'agency' ? MAX_AGENCY_TRAVELLERS : MAX_TRAVELLERS;
}

/** One row of «Who's coming?». The first is the creator — or, for an agency, the lead traveller. */
export interface TravellerDraft {
  key: string;
  name: string;
  /** `YYYY-MM-DD` or '' — optional (ADR-0029). */
  birthDate: string;
  email: string;
  showEmail: boolean;
  /** ADR-0037: in an agency's trip, every traveller is a client of its book. */
  clientId?: string;
}

/** ADR-0030: one destination of the trip, with its nights once dates are known. */
export interface LegDraft {
  key: string;
  name: string;
  lat?: number;
  lng?: number;
  nights: number;
}

export interface WizardForm {
  /** What is being typed in the destination search, not yet a leg. */
  destinationQuery: string;
  legs: LegDraft[];
  startDate: string;
  endDate: string;
  nights: number;
  travellers: TravellerDraft[];
  /** Tickets sorted by kind, read and saved once the trip exists. */
  tickets: TicketFiles;
  startMode: StartMode | null;
  /** From the documents branch: also run the AI passes over what's missing. */
  complementWithAi: boolean;
  selectedInterests: string[];
  pace: Pace;
  budget: Budget;
  notes: string;
  budgetCurrency: string;
  name: string;
  nameEdited: boolean;
}

export function initialForm(mode: WizardMode, ownerName: string, ownerBirthDate: string): WizardForm {
  // An agency picks its clients from its book (ADR-0037); nobody is travelling yet.
  const owner: TravellerDraft = { key: 'owner', name: ownerName, birthDate: ownerBirthDate, email: '', showEmail: false };
  return {
    destinationQuery: '', legs: [], startDate: '', endDate: '', nights: 1,
    travellers: mode.kind === 'agency' ? [] : [owner],
    tickets: EMPTY_TICKETS, startMode: null, complementWithAi: false,
    selectedInterests: [], pace: 'balanced', budget: 'mid', notes: '',
    budgetCurrency: guessCurrencyFromLocale(),
    name: '', nameEdited: false,
  };
}

export function wantsAi(form: WizardForm): boolean {
  return form.startMode === 'ai' || (form.startMode === 'documents' && form.complementWithAi);
}

/** The trip's destinations as one display name ("Uzbekistán · Kirguistán"). */
export function destinationOf(form: WizardForm): string {
  return summaryName(form.legs);
}

/** The steps this run goes through, given the answers so far. */
export function stepSequence(form: WizardForm, mode: WizardMode): Step[] {
  // The nights split only exists for a trip with several destinations, and a
  // circuit template has nobody travelling yet — so no tickets of theirs either.
  const isTemplate = mode.kind === 'agency' && mode.isTemplate;
  const base: Step[] = [
    'destination', 'dates', ...(form.legs.length > 1 ? ['legs' as const] : []),
    ...(isTemplate ? [] : ['travellers' as const, 'bookings' as const]), 'start',
  ];
  if (form.startMode === 'documents') base.push('documents');
  if (wantsAi(form)) base.push('style');
  base.push('summary');
  return base;
}
